import {IEmployeePreview} from "api/types";
import ProfileImage from "components/ProfileImage";
import Tooltip from "components/Tooltip";
import {GYM_COURSE_ID} from "constants/xp";
import {FC} from "react";
import {useNavigate} from "react-router";

interface Props {
  employee: IEmployeePreview;
}

const SEGMENT_INACTIVE = "#E8E4DF";
const segmentActiveColors = ["#3D52AD", "#F07340", "#57B98F", "#9E77ED"] as const;

function getLevel(completed: number, total: number): "low" | "average" | "high" {
  if (!total) return "low";
  const pct = (completed / total) * 100;
  if (pct <= 25) return "low";
  if (pct <= 75) return "average";
  return "high";
}

function segmentColor(level: "low" | "average" | "high", index: number) {
  return level === "high" ? segmentActiveColors[index] : SEGMENT_INACTIVE;
}

function initials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

const levelLabel: Record<"low" | "average" | "high", string> = {
  low: "Low",
  average: "Average",
  high: "High",
};
const levelColors: Record<"low" | "average" | "high", {bg: string; text: string}> = {
  low: {bg: "#FEF0EC", text: "#F57A47"},
  average: {bg: "#FFF8EC", text: "#F5A623"},
  high: {bg: "#EDFAF4", text: "#57B98F"},
};

interface ProgressPillProps {
  label: string;
  completed: number;
  total: number;
}

const ProgressPill: FC<ProgressPillProps> = ({label, completed, total}) => {
  const level = getLevel(completed, total);
  const {bg, text} = levelColors[level];
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-xs font-semibold" style={{color: text}}>
        {completed ?? 0} / {total ?? 0}
      </span>
      <span className="text-[10px] text-brand-muted">{label}</span>
    </div>
  );
};

const Employee: FC<Props> = ({employee}) => {
  const navigate = useNavigate();

  const foundations = employee.courses[955112];
  const cleanup = employee.courses[577780];
  const practice = employee.courses[GYM_COURSE_ID];
  const {milestones, milestonesCompletedList} = employee;

  const foundationsLevel = getLevel(foundations?.completed, foundations?.total);
  const cleanupLevel = getLevel(cleanup?.completed, cleanup?.total);
  const practiceLevel = getLevel(practice?.completed, practice?.total);
  const milestonesLevel = getLevel(milestones?.completed, milestones?.total);

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      style={{border: "1px solid #E8E4DF", boxShadow: "rgba(51,41,28,0.05) 0px 2px 12px"}}
      onClick={() => navigate(`/accountant/${employee.id}`, {state: {backLabel: "Back to Employees"}})}
    >
      {/* 4-segment metric bar */}
      <div className="flex h-1.5">
        <div className="flex-1" style={{background: segmentColor(foundationsLevel, 0)}} />
        <div className="flex-1" style={{background: segmentColor(cleanupLevel, 1)}} />
        <div className="flex-1" style={{background: segmentColor(practiceLevel, 2)}} />
        <div className="flex-1" style={{background: segmentColor(milestonesLevel, 3)}} />
      </div>

      {/* Card body */}
      <div className="px-5 py-4 flex items-center gap-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {employee.image ? (
            <ProfileImage src={employee.image} size={48} />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold"
              style={{backgroundColor: "#E5E1DC", color: "#282D4D"}}
            >
              {initials(employee.name)}
            </div>
          )}
        </div>

        {/* Name + role */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-brand-navy truncate">{employee.name}</h3>
          <p className="text-xs text-brand-muted capitalize">{employee.role || "—"}</p>
        </div>

        {/* Progress pills */}
        <div className="flex items-center gap-6 flex-shrink-0">
          <ProgressPill label="Foundations" completed={foundations?.completed} total={foundations?.total} />
          <ProgressPill label="Cleanup" completed={cleanup?.completed} total={cleanup?.total} />
          <ProgressPill label="Practice" completed={practice?.completed} total={practice?.total} />
          <Tooltip
            text={milestonesCompletedList.map((m) => [m.description, m.isCompleted ? "✓" : "✗"].join(" ")).join("\n")}
            className="whitespace-pre-line"
            position="left"
          >
            <ProgressPill label="Milestones" completed={milestones?.completed} total={milestones?.total} />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default Employee;
